import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { log } from "../lib/log";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

/**
 * CosmicWeatherAlerts — dashboard nudge card that surfaces rare planetary transits
 * touching the user's saved cities this week. Alerts refresh every Monday.
 * Dismissible per ISO week (stored in localStorage) so we don't nag if the user closes it.
 */
export default function CosmicWeatherAlerts() {
  const [data, setData] = useState(null);
  const [dismissedWeek, setDismissedWeek] = useState(() => {
    try { return localStorage.getItem("stargrid.alertsDismissedWeek") || ""; } catch { return ""; }
  });

  useEffect(() => {
    let alive = true;
    api.get("/alerts/cosmic-weather")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, []);

  if (!data || !data.alerts || data.alerts.length === 0) return null;
  if (dismissedWeek === data.iso_week) return null;

  const dismiss = () => {
    try { localStorage.setItem("stargrid.alertsDismissedWeek", data.iso_week); } catch (e) { log.warn("localStorage unavailable:", e); }
    setDismissedWeek(data.iso_week);
  };

  return (
    <section className="mt-10" data-testid="cosmic-weather-alerts">
      <div className="relative rounded-2xl border border-[#C8A96E]/25 bg-gradient-to-br from-[#1C1622] via-[#15111A] to-[#15111A] p-5 sm:p-6 overflow-hidden">
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-30" style={{ background: "radial-gradient(circle, #C8A96E 0%, transparent 60%)" }} />
        <button
          onClick={dismiss}
          data-testid="cosmic-weather-dismiss"
          aria-label="Dismiss for this week"
          className="absolute top-3 right-3 text-[#A89C98] hover:text-white transition-colors"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#C8A96E]">
          <Sparkles size={12} />
          <span>Cosmic Weather · {data.iso_week}</span>
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl text-white mt-2" data-testid="cosmic-weather-title">
          Rare transits touching your cities this week
        </h2>
        <p className="text-sm text-[#A89C98] mt-1">
          Deterministic per week — refreshes every Monday. Time your visit while the resonance holds.
        </p>

        <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data.alerts.map((a, i) => (
            <div
              key={`${a.city}-${a.planet}-${i}`}
              data-testid={`cosmic-alert-${a.city.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/25 transition-colors"
              style={{ boxShadow: `inset 0 0 0 1px ${a.color}22` }}
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: a.color }}>
                  {a.planet} · Rare Transit
                </div>
                <div className="font-mono-scanner text-sm" style={{ color: a.color }}>
                  {a.rating}/10
                </div>
              </div>
              <div className="font-heading text-lg text-white mt-1">{a.city}</div>
              <p className="text-[12px] text-white/85 mt-2 leading-snug">{a.text}</p>
              <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#A89C98]">
                <span>{a.days_left}d left</span>
                <Link
                  to={`/scanner?city=${encodeURIComponent(a.city)}`}
                  data-testid={`cosmic-alert-scan-${a.city.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[#C8A96E] hover:text-[#f0d167]"
                >
                  Scan again →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
